import { useState, useCallback } from 'react';
import { API_BASE } from '../../config/api';
import { fetchRaw } from '../../lib/api-client';
import EndpointList from './EndpointList';
import ResponseViewer from './ResponseViewer';

export default function APIExplorer() {
  const [selectedEndpoint, setSelectedEndpoint] = useState<string | null>(null);
  const [data, setData] = useState<unknown>(null);
  const [responseTime, setResponseTime] = useState(0);
  const [status, setStatus] = useState(200);
  const [isLoading, setIsLoading] = useState(false);

  const handleSelect = useCallback(async (path: string) => {
    setSelectedEndpoint(path);
    setIsLoading(true);
    setData(null);

    try {
      const result = await fetchRaw(path);
      setData(result.json);
      setResponseTime(result.responseTime);
      setStatus(result.status);
    } catch (err) {
      setData({ error: err instanceof Error ? err.message : 'Request failed' });
      setStatus(500);
      setResponseTime(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <section id="explorer" className="space-y-6">
      {/* Section header */}
      <div>
        <h2 className="text-3xl font-bold text-ti-text mb-2">
          Interactive API Explorer
        </h2>
        <p className="text-ti-text-muted max-w-2xl">
          Click any endpoint to query Ted's Living Resume API in real-time.
          Every response is fetched live -- nothing is cached or mocked.
        </p>
      </div>

      {/* Explorer shell */}
      <div className="border border-ti-border rounded-xl overflow-hidden bg-ti-surface">
        {/* Terminal top bar */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-ti-bg border-b border-ti-border">
          {/* Traffic light dots */}
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-ti-red/60" />
            <span className="w-3 h-3 rounded-full bg-ti-amber/60" />
            <span className="w-3 h-3 rounded-full bg-ti-green/60" />
          </div>
          {/* URL bar */}
          <div className="flex-1 bg-ti-surface rounded px-3 py-1 text-xs font-mono text-ti-text-dim">
            {selectedEndpoint ? (
              <>
                <span className="text-ti-green">GET</span>{' '}
                <span className="text-ti-text-muted">{API_BASE}</span>
                <span className="text-ti-cyan">{selectedEndpoint}</span>
              </>
            ) : (
              <span>Select an endpoint to begin...</span>
            )}
          </div>
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-ti-blue animate-pulse" />
              <span className="text-xs text-ti-blue">Loading</span>
            </div>
          )}
        </div>

        {/* Two-panel layout */}
        <div className="flex flex-col md:flex-row" style={{ minHeight: '500px' }}>
          {/* Left: Endpoint list */}
          <div className="w-full md:w-[280px] border-b md:border-b-0 md:border-r border-ti-border overflow-hidden">
            <EndpointList
              selectedEndpoint={selectedEndpoint}
              onSelect={handleSelect}
            />
          </div>

          {/* Right: Response viewer */}
          <div className="flex-1 min-w-0">
            <ResponseViewer
              data={data}
              responseTime={responseTime}
              status={status}
              endpoint={selectedEndpoint || ''}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
